import type { CompetitionCatalogue } from "./competition";

export const CATALOGUE_BASE = "https://public.pinpointgolf.club/config/championship/v1/";
export function catalogueBase(environment: "prod" | "dev"): string {
	return environment === "dev" ? `${CATALOGUE_BASE}dev/` : CATALOGUE_BASE;
}
const REVISION = /^[a-zA-Z0-9][a-zA-Z0-9._-]{0,79}$/;

function object(value: unknown): Record<string, unknown> {
	if (!value || typeof value !== "object" || Array.isArray(value)) {
		throw new Error("Expected catalogue object");
	}
	return value as Record<string, unknown>;
}

function text(value: unknown, empty = false): asserts value is string {
	if (typeof value !== "string" || (!empty && !value.trim())) {
		throw new Error("Invalid catalogue text");
	}
}

function integer(value: unknown, minimum = 0): asserts value is number {
	if (!Number.isSafeInteger(value) || (value as number) < minimum) {
		throw new Error("Invalid catalogue integer");
	}
}

function array(value: unknown): unknown[] {
	if (!Array.isArray(value) || !value.length) throw new Error("Empty catalogue list");
	return value;
}

function unique(values: unknown[]): void {
	if (new Set(values).size !== values.length) throw new Error("Duplicate catalogue ID");
}

function tone(value: unknown): void {
	if (!["secondary", "success", "warning", "dark"].includes(String(value))) {
		throw new Error("Invalid catalogue tone");
	}
}

function version(value: Record<string, unknown>): void {
	if (value.schemaVersion !== 1 || typeof value.revision !== "string" ||
		!REVISION.test(value.revision)) throw new Error("Unsupported catalogue version");
}

export function parseManifest(value: unknown, base = CATALOGUE_BASE): { revision: string; url: string } {
	const manifest = object(value);
	version(manifest);
	const revision = String(manifest.revision);
	const url = new URL(`releases/${revision}.json`, base).href;
	if (typeof manifest.url !== "string" ||
		new URL(manifest.url, base).href !== url) {
		throw new Error("Invalid catalogue release URL");
	}
	return { revision, url };
}

export function parseCatalogue(value: unknown): CompetitionCatalogue {
	const catalogue = object(value);
	version(catalogue);
	const competition = object(catalogue.competition);
	integer(competition.id, 1);
	text(competition.name);
	text(competition.season);
	for (const key of ["startDate", "endDate"]) {
		const date = competition[key];
		text(date);
		if (!/^\d{4}-\d{2}-\d{2}$/.test(date) ||
			!Number.isFinite(Date.parse(date)) ||
			new Date(date).toISOString().slice(0, 10) !== date) {
			throw new Error("Invalid competition date");
		}
	}
	if (String(competition.startDate) > String(competition.endDate)) {
		throw new Error("Competition ends before it starts");
	}
	integer(competition.jackpotAmount);
	if (typeof competition.isActive !== "boolean") throw new Error("Invalid active flag");
	for (const key of ["prizes", "rewards"]) {
		const entries = array(catalogue[key]).map(object);
		unique(entries.map((entry) => entry.id));
		for (const entry of entries) {
			integer(entry.id, 1);
			text(entry.name);
			text(entry.description, true);
			integer(entry.pointsRequired, key === "rewards" ? 1 : 0);
			if (key === "prizes") {
				integer(entry.place, 1);
				if (entry.value !== undefined) integer(entry.value);
				if (entry.imageUrl !== undefined) {
					text(entry.imageUrl);
					if (new URL(entry.imageUrl).protocol !== "https:") throw new Error("Invalid prize image");
				}
			} else {
				if (entry.kind !== "recurring" && entry.kind !== "milestone") {
					throw new Error("Invalid reward kind");
				}
				tone(entry.tone);
			}
		}
		if (key === "prizes") {
			unique(entries.map((entry) => entry.place));
			if (!entries.some((entry) => entry.place === 1)) throw new Error("Missing grand prize");
		} else if (entries.filter((entry) => entry.kind === "recurring").length !== 1) {
			throw new Error("Expected one recurring entry reward");
		}
	}
	const categories = array(catalogue.pointCategories).map(object);
	unique(categories.map((category) => category.id));
	const itemIds: string[] = [];
	for (const category of categories) {
		text(category.id);
		text(category.name);
		text(category.description);
		for (const item of array(category.items).map(object)) {
			text(item.id);
			text(item.name);
			integer(item.points);
			tone(item.tone);
			itemIds.push(item.id);
		}
	}
	unique(itemIds);
	for (const id of ["follow-instagram", "follow-facebook", "subscribe-youtube", "follow-tiktok"]) {
		if (!itemIds.includes(id)) throw new Error(`Missing scoring item: ${id}`);
	}
	return value as CompetitionCatalogue;
}

export function rewardProgress(catalogue: CompetitionCatalogue, points: number) {
	const recurring = catalogue.rewards.find((reward) => reward.kind === "recurring");
	if (!recurring) throw new Error("Missing recurring entry reward");
	const milestone = catalogue.rewards
		.filter((reward) => reward.kind === "milestone" && reward.pointsRequired > points)
		.sort((a, b) => a.pointsRequired - b.pointsRequired)[0];
	return {
		nextEntryPoints: recurring.pointsRequired - points % recurring.pointsRequired,
		nextMilestone: milestone,
	};
}
